<template>
  <section ref="root" class="dc-container">
    <!-- papel rasgado -->
    <div class="rasgado rasgado-top"></div>

    <!-- CONTENIDO -->
    <div class="dc-card">
      <!-- Título en dos líneas -->
      <h2 ref="title" class="dc-title">
        <span>Código</span><br />
        <span class="script">de Vestimenta</span>
      </h2>

      <!-- Etiqueta -->
      <div ref="badge" class="dc-badge">FORMAL</div>

      <!-- Ilustración vestido / traje -->
      <img
        ref="figure"
        src="@/assets/codigo.png"
        alt="Código de vestimenta"
        class="dc-figure"
      />

      <!-- Nota -->
      <p ref="note" class="dc-note">
        Reservamos las tonalidades de azul para la Quinceañera, así como también Celeste y Rosa Gold. <br>
      </p>

      <!-- BOTÓN QUE ABRE EL MODAL -->
      <button ref="btnRef" class="dc-btn-especificaciones" @click="abrirModal">
        Especificaciones Dress Code
      </button>
    </div>

    <div class="rasgado rasgado-bottom"></div>

    <!-- MODAL DE ESPECIFICACIONES -->
    <div v-if="mostrarModal" class="dc-modal-overlay" @click.self="cerrarModal">
      <div class="dc-modal-wrapper">
        <!-- Botón para cerrar -->
        <button class="dc-modal-close" @click="cerrarModal">✕</button>
        
        <!-- Contenedor con scroll para cuando la imagen se amplíe -->
        <div class="dc-modal-body">
          <img 
            :src="require('@/assets/DressCode_XV_Edith.webp')" 
            alt="Especificaciones detalladas" 
            class="dc-modal-img"
            :class="{ 'is-zoomed': tieneZoom }"
            @dblclick="alternarZoom"
          />
        </div>
        
        <p class="dc-modal-hint">💡 Doble clic para ampliar / reducir</p>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, nextTick, onUnmounted } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'DressCodeLuxuryExact',
  setup() {
    const root = ref(null)
    const title = ref(null)
    const badge = ref(null)
    const figure = ref(null)
    const note = ref(null)
    const btnRef = ref(null)

    // Estados del Modal y Zoom
    const mostrarModal = ref(false)
    const tieneZoom = ref(false)

    let stInstance = null

    const abrirModal = () => {
      mostrarModal.value = true
      document.body.style.overflow = 'hidden' // Evita scroll de fondo
    }

    const cerrarModal = () => {
      mostrarModal.value = false
      tieneZoom.value = false // Resetea el zoom al cerrar
      document.body.style.overflow = ''
    }

    const alternarZoom = () => {
      tieneZoom.value = !tieneZoom.value
    }

    onMounted(async () => {
      await nextTick()

      if (!root.value) return

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.value,
          start: 'top 85%',
          once: true
        }
      })

      if (title.value) {
        tl.from(title.value, {
          opacity: 0,
          y: -24,
          filter: 'blur(6px)',
          duration: 0.5,
          ease: 'power3.out'
        })
      }

      if (badge.value) {
        tl.from(
          badge.value,
          {
            opacity: 0,
            y: -16,
            scale: 0.98,
            duration: 0.45,
            ease: 'back.out(1.6)'
          },
          '>-0.1'
        )
      }

      if (figure.value) {
        tl.from(
          figure.value,
          {
            opacity: 0,
            scale: 0.9,
            duration: 0.5,
            ease: 'power2.out'
          },
          '>-0.05'
        )
      }

      if (note.value) {
        tl.from(
          note.value,
          {
            opacity: 0,
            y: 14,
            duration: 0.4,
            ease: 'power2.out'
          },
          '>-0.05'
        )
      }

      if (btnRef.value) {
        tl.from(
          btnRef.value,
          {
            opacity: 0,
            y: 16,
            scale: 0.95,
            duration: 0.45,
            ease: 'power3.out'
          },
          '>-0.05'
        )
      }
    })

    onUnmounted(() => {
      if (stInstance) stInstance.kill()
      document.body.style.overflow = ''
      ScrollTrigger.refresh()
    })

    return { 
      root, 
      title, 
      badge, 
      figure, 
      note, 
      btnRef,
      mostrarModal,
      tieneZoom,
      abrirModal,
      cerrarModal,
      alternarZoom
    }
  }
}
</script>

<style scoped>
.dc-container {
  position: relative;
  max-width: 500px;
  margin: 0 auto;
  background: #8fbbdf;
  padding: 34px 18px 42px;
  overflow: hidden;
  text-align: center;
}

/* Rasgados */
.rasgado {
  position: absolute;
  left: 0;
  width: 100%;
  height: 40px;
  background: #8fbbdf;
  z-index: 1;
  pointer-events: none;
}
.rasgado-top {
  top: 0;
  clip-path: polygon(0% 70%, 18% 55%, 38% 78%, 58% 52%, 78% 75%, 100% 60%, 100% 0%, 0% 0%);
}
.rasgado-bottom {
  bottom: 0;
  clip-path: polygon(0% 30%, 18% 45%, 38% 22%, 58% 48%, 78% 25%, 100% 40%, 100% 100%, 0% 100%);
}

.dc-card {
  position: relative;
  z-index: 2;
}

.dc-title {
  font-family: 'britishgruesa', cursive;
  font-size: 66px;
  color: #ffffff;
  margin: 0 0 16px;
  line-height: 1.05;
}
.dc-title .script {
  font-weight: 400;
  opacity: 0.9;
}

.dc-badge {
  display: inline-block;
  background: #ffffff;
  color: #333;
  font-family: 'padres', sans-serif;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.14em;
  padding: 8px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  margin: 4px 0 12px;
}

.dc-figure {
  width: 180px;
  height: auto;
  display: block;
  margin: 10px auto 12px;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.15));
}

.dc-note {
  font-family: 'Playfair Display', serif;
  font-size: 19px;
  color: #ffffff;
  opacity: 0.9;
  font-style: italic;
  margin: 10px auto 18px;
}

/* BOTÓN ESPECIFICACIONES */
.dc-btn-especificaciones {
  display: inline-block;
  background: #ffffff;
  color: #333333;
  border: none;
  font-family: 'padres', sans-serif;
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 12px 24px;
  border-radius: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  margin: 15px auto 25px;
  transition: background-color 0.3s ease, transform 0.2s ease;
  cursor: pointer;
}

.dc-btn-especificaciones:active {
  transform: scale(0.97);
  background-color: #f5f5f5;
}

/* ================= MODAL ESTILO LUJO ================= */
.dc-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(5px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.dc-modal-wrapper {
  position: relative;
  background: #ffffff;
  width: 100%;
  max-width: 420px;
  max-height: 85vh;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
  display: flex;
  flex-direction: column;
}

.dc-modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.5);
  color: #ffffff;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 14px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}
.dc-modal-close:active {
  background: rgba(0, 0, 0, 0.8);
}

.dc-modal-body {
  overflow: auto; /* Permite scroll cuando la imagen se amplíe */
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fdfdfd;
  padding: 20px 10px;
}

.dc-modal-img {
  max-width: 100%;
  max-height: 70vh;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1), max-width 0.3s ease, max-height 0.3s ease;
  cursor: zoom-in;
  user-select: none;
}

/* Estado con zoom activado por doble click */
.dc-modal-img.is-zoomed {
  max-width: 200%;  /* Permite que la imagen se estire más allá del contenedor */
  max-height: none;
  transform: scale(1.3); /* Multiplica el tamaño */
  cursor: zoom-out;
}

.dc-modal-hint {
  font-family: 'Playfair Display', serif;
  font-size: 13px;
  color: #666666;
  background: #f5f5f5;
  margin: 0;
  padding: 10px;
  border-top: 1px solid #eeeeee;
}

@media (max-width: 380px) {
  .dc-title { font-size: 56px; }
  .dc-btn-especificaciones { font-size: 11px; padding: 10px 20px; }
}
</style>